import { useCallback, useState } from "react";

import { SubscriptionPlans } from "@prisma/client";
import { isAxiosError } from "axios";
import { useSession } from "next-auth/react";
import { createPortal } from "react-dom";
import { v4 as uuid } from "uuid";

import Button, { type IButtonProps } from "@musicfy/components/Button";
import Icon from "@musicfy/components/Icon";
import StartCheckoutFlowButton from "@musicfy/components/PaymentButtons/StartCheckoutFlowButton";
import ProgressBar from "@musicfy/components/ProgressBar";
import { useAnalytics } from "@musicfy/libs/AnalyticsProvider";
import { useAudioGenerations } from "@musicfy/libs/AudioGenerationsProvider/hooks";
import {
  type TAudioGeneration,
  type TBaseAudioGeneration,
} from "@musicfy/libs/AudioGenerationsProvider/IAudioGenerations";
import { useConvertUsageCounterContext } from "@musicfy/libs/ConvertUsageCounterProvider";
import { useGlobalAudioPlayerContext } from "@musicfy/libs/GlobalAudioPlayerProvider";
import { type IAudioSource } from "@musicfy/libs/GlobalAudioPlayerProvider/types";
import modalAxios, { ModalFunction } from "@musicfy/libs/modalAxios";
import { type IModel } from "@musicfy/libs/ModelsProvider/IModels";
import { useRequireAuthContext } from "@musicfy/libs/RequireAuthProvider";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";
import { useToastContext } from "@musicfy/libs/ToastProvider";
import { type IFileUpload } from "@musicfy/utils/hooks/useStorage";

import {
  type TInferenceSettings,
  DEFAULT_INFERENCE_SETTINGS,
  type OnboardingAction,
} from "./types";
import {
  dispatchValueMoment,
  isFreePlan,
} from "../NotificationBanners/valueMoment";

const BASE_TEXT_TO_DISPLAY = [
  "Extracting audio samples",
  "Loading artist vocals",
];

const ISOLATE_VOCALS_TEXT = "Isolating Vocals";
const ENHANCE_VOCALS_TEXT = "Enhancing Vocals";
const FINALIZING_TEXT = "Voice cloning in progress";

/** Cap parallel Modal convert calls so isolate/reverb jobs do not stampede GPUs. */
const INFERENCE_CONCURRENCY = 2;

// Product-framed: long clips + cleanup toggles exceed the convert time budget —
// not an outage message.
const TIMEOUT_ERROR_MESSAGE =
  "This track needs a lighter convert. Try one shorter file, or turn off Remove Instrumentals / Remove Reverb for longer audio.";

const GENERIC_ERROR_MESSAGE =
  "We couldn't finish this convert with the current settings. Try a shorter clip, fewer files, or turn off Remove Instrumentals / Remove Reverb.";

interface RemixButtonProps extends IButtonProps {
  selectedModels: IModel[];
  uploadedFiles: IFileUpload[];
  setErrorMessage?: React.Dispatch<React.SetStateAction<string>>;
  inferenceSettings?: TInferenceSettings;
  dispatchOnboarding: React.Dispatch<OnboardingAction>;
  isUploading?: boolean;
}

const mapBackendDetailToUserMessage = (detail: string): string => {
  const normalized = detail.toLowerCase();
  if (normalized.includes("remove instrumental")) {
    return "Remove Instrumentals couldn't cleanly separate this mix. Try a vocal-only clip, or turn the toggle off and convert again.";
  }
  if (normalized.includes("reverb")) {
    return "Remove Reverb/Echo couldn't finish on this clip. Try a drier recording, or turn the toggle off and convert again.";
  }
  if (
    normalized.includes("corrupt") ||
    normalized.includes("audio clip not found")
  ) {
    return "That audio file couldn't be processed. Re-upload it and try again.";
  }
  if (normalized.includes("daily conversion limit")) {
    return "You've reached today's conversion limit.";
  }
  return detail;
};

const formatInferenceErrorDetail = (detail: unknown): string | null => {
  if (detail == null) {
    return null;
  }
  if (typeof detail === "string") {
    return mapBackendDetailToUserMessage(detail);
  }
  if (typeof detail === "object" && detail !== null && "message" in detail) {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string" && message.length > 0) {
      return mapBackendDetailToUserMessage(message);
    }
  }
  try {
    return JSON.stringify(detail);
  } catch {
    return null;
  }
};

const getInferenceErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
      return TIMEOUT_ERROR_MESSAGE;
    }
    if (!err.response) {
      return TIMEOUT_ERROR_MESSAGE;
    }
    const responseData = err.response.data as { detail?: unknown } | undefined;
    const detail = formatInferenceErrorDetail(responseData?.detail);
    if (detail) {
      return detail;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  if ((err as any)?.data?.httpStatus === 429) {
    return "quota";
  }

  // tRPC rejections — a lapsed plan, a locked voice, a model you do not own — carry the
  // server's message here. Only the 429 above was ever read, so every other rejection
  // fell through to the generic string below: someone whose subscription had ended was
  // told "something went wrong" and reported a bug, which is exactly how a support
  // thread turns into a multi-day investigation.
  //
  // Gate on `data.code`, which only a tRPC error carries. A plain JS Error also has
  // `.message`, and "Cannot read properties of undefined" is not something to show a user.
  const trpcError = (err ?? {}) as {
    data?: { code?: unknown };
    message?: unknown;
  };
  if (
    typeof trpcError.data?.code === "string" &&
    typeof trpcError.message === "string" &&
    trpcError.message.trim()
  ) {
    return trpcError.message;
  }

  return GENERIC_ERROR_MESSAGE;
};

/**
 * Run async work with a fixed concurrency limit. Preserves input order in results.
 */
async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = items.map(() => ({
    status: "rejected",
    reason: new Error("Inference worker did not run"),
  }));
  let nextIndex = 0;

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    async () => {
      while (nextIndex < items.length) {
        const current = nextIndex;
        nextIndex += 1;
        const item = items[current];
        if (item === undefined) {
          continue;
        }
        try {
          const value = await mapper(item, current);
          results[current] = { status: "fulfilled", value };
        } catch (reason) {
          results[current] = { status: "rejected", reason };
        }
      }
    }
  );

  await Promise.all(workers);
  return results;
}

const InferenceButton = ({
  selectedModels,
  uploadedFiles,
  setErrorMessage,
  inferenceSettings = DEFAULT_INFERENCE_SETTINGS,
  dispatchOnboarding,
  isUploading = false,
  ...buttonProps
}: RemixButtonProps): JSX.Element => {
  const {
    shouldIsolateVocals,
    f0Method,
    transpose,
    shouldEnhanceVocals,
    formantFactor,
    cleanAudio,
    cleanStrength,
  } = inferenceSettings;

  const { showAuthModal } = useRequireAuthContext();
  const { subscription } = useSubscriptionContext();
  const { sendToastMessage } = useToastContext();

  const { data } = useSession();
  const { trackEvent } = useAnalytics();
  const { createAudioGenerations, setAudioGenerations } = useAudioGenerations();
  const { decrementGenerations } = useConvertUsageCounterContext();
  const { setAudioSources } = useGlobalAudioPlayerContext();

  const [quotaExceeded, setQuotaExceeded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const cloneAudio = useCallback(async () => {
    dispatchOnboarding({ type: "CLOSE_ONBOARDING" });
    const user = data?.user;
    if (!user) {
      showAuthModal();
      return;
    }

    setErrorMessage?.("");

    if (isUploading) {
      sendToastMessage("Please wait for your upload to finish", {
        severity: "info",
      });
      return;
    }

    if (!selectedModels.length) {
      sendToastMessage("Please select an artist", { severity: "error" });
      return;
    }

    if (!uploadedFiles.length) {
      sendToastMessage("Please upload an audio file", { severity: "error" });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setAudioSources([]);

    const selectedModelsWithUploadedFile = [];
    for (let i = 0; i < uploadedFiles.length; i++) {
      const uploadedFile = uploadedFiles[i];

      if (!uploadedFile) {
        setIsLoading(false);
        setErrorMessage?.(
          "Something went wrong with your upload. Please try again."
        );
        return;
      }

      for (let j = 0; j < selectedModels.length; j++) {
        const model = selectedModels[j];

        if (!model) {
          setIsLoading(false);
          setErrorMessage?.("Please select an artist and try again.");
          return;
        }

        const audioId = uploadedFile.path.split("/")[1] || "";

        selectedModelsWithUploadedFile.push({
          inferenceId: uuid(),
          audioId: audioId,
          filename: uploadedFile.filename,
          modelId: model.id,
          artist: model.artist,
        });
      }
    }

    const audioGenerationsToCreate: TBaseAudioGeneration[] =
      selectedModelsWithUploadedFile.map(
        ({ inferenceId, audioId, filename, modelId }) => {
          return {
            id: inferenceId,
            audioId: audioId,
            filename: filename,
            modelId: modelId,
            isSaved: false,
            type: "vocals",
            displayName: filename,
            status: "processing",
            userId: user.id,
          };
        }
      );

    try {
      const _audioGenerations = await createAudioGenerations(
        audioGenerationsToCreate
      );

      // createAudioGenerations returns the new rows merged with the existing history,
      // so iterate the list we asked for and look the token up by id — mapping the
      // merged array would fire a conversion for every past generation.
      const modalTokensById = new Map(
        _audioGenerations.map((ag) => [ag.id, ag.modalToken])
      );

      const settled = await mapWithConcurrency(
        audioGenerationsToCreate,
        INFERENCE_CONCURRENCY,
        async ({ id }) => {
          const modalToken = modalTokensById.get(id);
          const res = await modalAxios(
            ModalFunction.INFERENCE,
            modalToken
          ).post<TAudioGeneration[]>("", {
            user_id: user.id,
            inference_id: id,
            should_isolate_vocals: shouldIsolateVocals,
            should_enhance_vocals: shouldEnhanceVocals,
            f0_method: f0Method,
            transpose: transpose,
            formant_factor: formantFactor,
            clean_audio: cleanAudio,
            clean_strength: cleanStrength,
          });

          return res.data;
        }
      );

      const completedAudioGenerations: TAudioGeneration[] = [];
      const failureMessages: string[] = [];
      let hitQuota = false;

      settled.forEach((result) => {
        if (result.status === "fulfilled") {
          completedAudioGenerations.push(...result.value);
          return;
        }

        const message = getInferenceErrorMessage(result.reason);
        if (message === "quota") {
          hitQuota = true;
          return;
        }
        failureMessages.push(message);
      });

      if (hitQuota && completedAudioGenerations.length === 0) {
        setQuotaExceeded(true);
        setIsLoading(false);
        return;
      }

      const totalRequested = audioGenerationsToCreate.length;
      const failedCount = settled.filter((r) => r.status === "rejected").length;

      if (completedAudioGenerations.length === 0) {
        const firstDetail = failureMessages[0] ?? GENERIC_ERROR_MESSAGE;
        setErrorMessage?.(`🚧 ${firstDetail}`);
        setIsLoading(false);
        return;
      }

      const updatedAudioGenerations = [..._audioGenerations];

      completedAudioGenerations.forEach((generation) => {
        const index = updatedAudioGenerations.findIndex(
          (ag) => ag.id === generation.id
        );

        if (index < 0) {
          updatedAudioGenerations.unshift(generation);
          return;
        }

        updatedAudioGenerations[index] = generation;
      });

      const audioSources: IAudioSource[] = completedAudioGenerations.map(
        (audioGeneration) => {
          return {
            id: audioGeneration.id,
            value: audioGeneration.url,
            label: audioGeneration.type,
            filename: audioGeneration.filename,
            artist: audioGeneration.model?.artist || "Unknown Artist",
          };
        }
      );

      setIsLoading(false);
      setAudioSources(audioSources);
      setAudioGenerations(updatedAudioGenerations);

      const succeededIds = new Set(
        completedAudioGenerations.map((generation) => generation.id)
      );
      selectedModelsWithUploadedFile.forEach((model) => {
        if (!succeededIds.has(model.inferenceId)) {
          return;
        }
        decrementGenerations();
        trackEvent("Cloned Audio", {
          Status: "Success",
          Subscription: subscription?.plan || SubscriptionPlans.free,
          Model: model.artist,
          f0Method: f0Method,
          transpose: transpose.toString(),
        });
      });

      if (failedCount > 0) {
        const firstDetail = failureMessages[0];
        setErrorMessage?.(
          `🚧 ${failedCount} of ${totalRequested} converts couldn't finish${
            firstDetail ? `: ${firstDetail}` : ""
          }`
        );
      }

      // Value moment: a FREE user just made something. Surface the watermark/HD
      // upsell (listener re-checks free + once-per-48h; this is the trigger
      // only). Known-free only — never dispatch on unknown/loading/paid so a
      // payer whose plan fetch blips is never even signalled.
      if (isFreePlan(subscription?.plan)) {
        dispatchValueMoment({
          topVoice: selectedModelsWithUploadedFile[0]?.artist,
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.log(err);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const isQuotaLimit = err?.data?.httpStatus === 429;
      if (isQuotaLimit) {
        setQuotaExceeded(true);
      } else {
        setErrorMessage?.(`🚧 ${getInferenceErrorMessage(err)}`);
      }

      setIsLoading(false);
    }
  }, [
    dispatchOnboarding,
    data?.user,
    isUploading,
    setErrorMessage,
    selectedModels,
    uploadedFiles,
    setAudioSources,
    showAuthModal,
    sendToastMessage,
    createAudioGenerations,
    setAudioGenerations,
    shouldIsolateVocals,
    shouldEnhanceVocals,
    f0Method,
    transpose,
    formantFactor,
    cleanAudio,
    cleanStrength,
    decrementGenerations,
    trackEvent,
    subscription?.plan,
  ]);

  if (quotaExceeded) {
    return (
      <>
        <StartCheckoutFlowButton
          origin="convert-voice-generation-limit"
          fullWidth
          preIcon={<Icon name="crown" />}
        >
          Upgrade
        </StartCheckoutFlowButton>
        <div className="mt-4 xl:block hidden text-center font-bold text-red/80">
          You&apos;ve reached your generation limit.
        </div>
      </>
    );
  }

  if (isLoading) {
    // Isolate/reverb dominate wall time; estimates track the raised Modal ceiling
    // so the progress bar does not finish while work is still running.
    const loadingTime =
      45 +
      (shouldIsolateVocals ? 90 : 0) +
      (shouldEnhanceVocals ? 90 : 0) +
      (uploadedFiles.length > 1
        ? Math.ceil((uploadedFiles.length - 1) / INFERENCE_CONCURRENCY) * 60
        : 0);
    let loadingText = [...BASE_TEXT_TO_DISPLAY];
    loadingText = loadingText.concat(
      shouldIsolateVocals ? [ISOLATE_VOCALS_TEXT] : []
    );
    loadingText = loadingText.concat(
      shouldEnhanceVocals ? [ENHANCE_VOCALS_TEXT] : []
    );
    loadingText.push(FINALIZING_TEXT);

    return (
      <>
        <Button fullWidth loading disabled {...buttonProps}>
          <div className="flex items-center w-full justify-center gap-3">
            <Icon name="logo-white" />
            <div>Generating...</div>
          </div>
        </Button>
        {createPortal(
          <div className="w-full flex relative items-stretch max-h-48 bg-gray-1000/90 backdrop-blur-2xl border-t border-white/15 px-6 py-6">
            <ProgressBar
              textToDisplay={loadingText}
              totalSeconds={loadingTime}
            />
          </div>,
          document.getElementById("global-audio-player-container") ??
            document.body,
          "global-audio-player"
        )}
      </>
    );
  }

  return (
    <div>
      <Button fullWidth onClick={cloneAudio} {...buttonProps}>
        <div className="flex items-center w-full justify-center gap-3">
          <Icon name="logo-white" />
          <div>Generate</div>
        </div>
      </Button>
    </div>
  );
};

export default InferenceButton;
